import React from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { setCloseEditInvoiceModal } from "../../redux/features/invoice/invoiceSlice";
import { useFormik } from "formik";
import { updateInvoice } from "../../redux/features/invoice/invoiceAction";
import * as Yup from "yup";
import { MESSAGES } from "../../utlis/messages";

const invoiceValidationSchema = Yup.object({
    description: Yup.string(),
    quantity: Yup.number()
        .typeError("Quantity must be a valid number")
        .integer("Quantity must be an integer")
        .min(1, "Minimum quantity: 1")
        .max(2000, "Maximum quantity: 1000")
        .required("Quantity is required"),
    price: Yup.number()
        .typeError("Price must be a valid number")
        .min(0.01, "Minimum price: 0.01")
        .required("Price is required"),
    production_lot: Yup.string()
        .typeError("Production lot must be valid text"),
    invoice_date: Yup.date()
        .typeError("Invalid invoice date")
        .required("Invoice date is required"),
    expected_delivery_date: Yup.date()
        .typeError("Invalid expected delivery date"),
    currency: Yup.string()
        .typeError("Currency must be valid text"),
    number_of_box: Yup.number()
        .typeError("Number of boxes must be a valid number")
        .integer("Number of boxes must be an integer")
        .min(0, "Minimum number of boxes: 0"),
    number_pallets: Yup.number()
        .typeError("Number of boxes must be a valid number")
        .integer("Number of boxes must be an integer")
        .min(0, "Minimum number of boxes: 0"),

    gross_weight: Yup.number().when("country_of_manufacturer", {
        is: (val: string) => !!val,
        then: schema => schema.required("Gross weight is required when country is provided"),
        otherwise: schema => schema.min(0, "Gross weight cannot be negative"),
    }),
    net_weight: Yup.number().when("country_of_manufacturer", {
        is: (val: string) => !!val,
        then: schema => schema.required("Net weight is required when country is provided"),
        otherwise: schema => schema.min(0, "Net weight cannot be negative"),
    }),



    shipping_mode: Yup.string()
        .oneOf(["truck", "boat"], "Shipping mode must be either 'truck' or 'boat'")
        .required("Shipping mode is required"),

    bill_lading_number: Yup.string().when("shipping_mode", (shipping_mode: any, schema) => {
        const mode = Array.isArray(shipping_mode) ? shipping_mode[0] : shipping_mode;
        return mode === "boat"
            ? schema.required("Bill of lading number is required for boat shipments")
            : schema;
    }),



});


const EditInvoiceModal = () => {
    const dispatch: any = useDispatch();
    const { editInvoiceModalOpen, editInvoiceData } = useSelector(
        (state: any) => state.invoiceSlice
    );

    const handleClose = () => {
        dispatch(setCloseEditInvoiceModal());
    };

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            id: editInvoiceData?.id,
            invoice_number: editInvoiceData?.invoice_number,
            order_number: editInvoiceData?.order_number || "",
            article_code: editInvoiceData?.article_code || "",
            quantity: editInvoiceData?.quantity || 0,
            price: editInvoiceData?.price || 0,
            description: editInvoiceData?.description || "",
            currency: editInvoiceData?.currency || "",
            expected_delivery_date: editInvoiceData?.expected_delivery_date || "",
            invoice_date: editInvoiceData?.invoice_date || "",
            production_lot: editInvoiceData?.production_lot,
            number_of_box: editInvoiceData?.number_of_box || 0,
            number_pallets: editInvoiceData?.number_pallets || 0,
            gross_weight: editInvoiceData?.gross_weight || 0,
            net_weight: editInvoiceData?.net_weight || 0,
            bill_lading_number: editInvoiceData?.bill_lading_number || "",
            isExported: editInvoiceData?.isExported || false,
            shipping_mode: editInvoiceData?.shipping_mode || "",
            country_of_manufacturer: editInvoiceData?.country_of_manufacturer || "",

        },
        validationSchema: invoiceValidationSchema,
        onSubmit: async (values) => {
            try {
                await dispatch(updateInvoice({ values }));
                handleClose();
            } catch (error) {
                console.error("Update invoice failed:", error);
            }
        },
    });

    return (
        <Modal show={editInvoiceModalOpen} onHide={handleClose}>
            <Modal.Header closeButton>
                <Modal.Title>Edit inventory item</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form onSubmit={formik.handleSubmit}>


                    <Form.Group className="mb-3">
                        <Form.Label>Invoice number</Form.Label>
                        {/* <div className="form-text text-muted mb-1">
                            Barcode exactly as scanned (EAN-8 or EAN-13). Numbers only.<br />
                            Only 8 or 13 digits. No spaces or symbols.
                        </div> */}
                        <Form.Control
                            type="text"
                            name="invoice_number"
                            value={formik.values.invoice_number}
                            onChange={formik.handleChange}
                            isInvalid={formik.touched.invoice_number && !!formik.errors.invoice_number}
                            disabled
                        />
                        {/* <Form.Control.Feedback type="invalid">
                            {formik.touched.ean && (formik.errors.ean?.toString() ?? '')}
                        </Form.Control.Feedback> */}
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Order Number</Form.Label>
                        {/* <div className="form-text text-muted mb-1">
                            Barcode exactly as scanned (EAN-8 or EAN-13). Numbers only.<br />
                            Only 8 or 13 digits. No spaces or symbols.
                        </div> */}
                        <Form.Control
                            type="text"
                            name="order_number"
                            value={formik.values.order_number}
                            onChange={formik.handleChange}
                            isInvalid={formik.touched.order_number && !!formik.errors.order_number}
                            disabled
                        />
                        {/* <Form.Control.Feedback type="invalid">
                            {formik.touched.ean && (formik.errors.ean?.toString() ?? '')}
                        </Form.Control.Feedback> */}
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Caleffi Article Code</Form.Label>
                        {/* <div className="form-text text-muted mb-1">
                            Barcode exactly as scanned (EAN-8 or EAN-13). Numbers only.<br />
                            Only 8 or 13 digits. No spaces or symbols.
                        </div> */}
                        <Form.Control
                            type="text"
                            name="article_code"
                            value={formik.values.article_code}
                            onChange={formik.handleChange}
                            isInvalid={formik.touched.article_code && !!formik.errors.article_code}
                            disabled
                        />
                        {/* <Form.Control.Feedback type="invalid">
                            {formik.touched.ean && (formik.errors.ean?.toString() ?? '')}
                        </Form.Control.Feedback> */}
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Currency</Form.Label>
                        {/* <div className="form-text text-muted mb-1">
                            Identificatore univoco del prodotto. Solo lettere, numeri, trattini o underscore.<br />
                            Obbligatorio. Lunghezza massima: 30 caratteri. Deve essere univoco.
                        </div> */}
                        <Form.Control
                            type="text"
                            name="currency"
                            value={formik.values.currency}
                            onChange={formik.handleChange}
                            isInvalid={formik.touched.currency && !!formik.errors.currency}
                        // disabled
                        />
                        <Form.Control.Feedback type="invalid">
                            {formik.touched.currency && (formik.errors.currency?.toString() ?? '')}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Quantity</Form.Label>
                        {/* <div className="form-text text-muted mb-1">
                            Number of units sold. Must be a positive integer.
                            Min: 1, Max: 1,000
                        </div> */}
                        <Form.Control
                            type="number"
                            name="quantity"
                            value={formik.values.quantity}
                            onChange={formik.handleChange}
                            isInvalid={formik.touched.quantity && !!formik.errors.quantity}
                        />
                        <Form.Control.Feedback type="invalid">
                            {formik.touched.quantity && (formik.errors.quantity?.toString() ?? '')}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Price</Form.Label>
                        {/* <div className="form-text text-muted mb-1">
                            Unit selling price (excluding VAT). Use the period `.` as the decimal separator.<br />
                            Min: 0.01, Max: 10,000.00
                        </div> */}
                        <Form.Control
                            type="text"
                            name="price"
                            value={formik.values.price}
                            onChange={formik.handleChange}
                            isInvalid={formik.touched.price && !!formik.errors.price}
                        />
                        <Form.Control.Feedback type="invalid">
                            {formik.touched.price && (formik.errors.price?.toString() ?? '')}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Production lot</Form.Label>
                        {/* <div className="form-text text-muted mb-1">
                            Unit cost for the company (excluding VAT). Must be ≤ the sale price.<br />
                            Min: 0.01, Max: 5,000.00. Must be ≤ the sale price.
                        </div> */}
                        <Form.Control
                            type="text"
                            name="production_lot"
                            value={formik.values.production_lot}
                            onChange={formik.handleChange}
                            isInvalid={formik.touched.production_lot && !!formik.errors.production_lot}
                        />
                        <Form.Control.Feedback type="invalid">
                            {formik.touched.production_lot && (formik.errors.production_lot?.toString() ?? '')}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Description</Form.Label>
                        {/* <div className="form-text text-muted mb-1">
                            Percentuale IVA, scritta come numero intero (senza simbolo %).<br />
                            Valori accettati: 0, 4, 10, 22
                        </div> */}
                        <Form.Control
                            type="text"
                            name="description"
                            value={formik.values.description}
                            onChange={formik.handleChange}
                            isInvalid={formik.touched.description && !!formik.errors.description}
                        />
                        <Form.Control.Feedback type="invalid">
                            {formik.touched.description && (formik.errors.description?.toString() ?? '')}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Invoice Date</Form.Label>
                        <Form.Control
                            type="date"
                            name="invoice_date"
                            value={formik.values.invoice_date?.substring(0, 10)}
                            onChange={formik.handleChange}
                            isInvalid={formik.touched.invoice_date && !!formik.errors.invoice_date}
                        />
                        <Form.Control.Feedback type="invalid">
                            {formik.touched.invoice_date && (formik.errors.invoice_date?.toString() ?? '')}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Expected delivery Date</Form.Label>
                        <Form.Control
                            type="date"
                            name="expected_delivery_date"
                            value={formik.values.expected_delivery_date?.substring(0, 10)}
                            onChange={formik.handleChange}
                            isInvalid={formik.touched.expected_delivery_date && !!formik.errors.expected_delivery_date}
                        />
                        <Form.Control.Feedback type="invalid">
                            {formik.touched.expected_delivery_date && (formik.errors.expected_delivery_date?.toString() ?? '')}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Number of Box</Form.Label>
                        {/* <div className="form-text text-muted mb-1">
                            Unit cost for the company (excluding VAT). Must be ≤ the sale price.<br />
                            Min: 0.01, Max: 5,000.00. Must be ≤ the sale price.
                        </div> */}
                        <Form.Control
                            type="text"
                            name="number_of_box"
                            value={formik.values.number_of_box}
                            onChange={formik.handleChange}
                            isInvalid={formik.touched.number_of_box && !!formik.errors.number_of_box}
                        />
                        <Form.Control.Feedback type="invalid">
                            {formik.touched.number_of_box && (formik.errors.number_of_box?.toString() ?? '')}
                        </Form.Control.Feedback>
                    </Form.Group>

                     {/* <Form.Group className="mb-3">
                        <Form.Label>Number of Pallets</Form.Label>
                       
                        <Form.Control
                            type="text"
                            name="number_pallets"
                            value={formik.values.number_pallets}
                            onChange={formik.handleChange}
                            isInvalid={formik.touched.number_pallets && !!formik.errors.number_pallets}
                        />
                        <Form.Control.Feedback type="invalid">
                            {formik.touched.number_pallets && (formik.errors.number_pallets?.toString() ?? '')}
                        </Form.Control.Feedback>
                    </Form.Group> */}

                    <Form.Group className="mb-3">
                        <Form.Label>Gross Weight</Form.Label>
                        {/* <div className="form-text text-muted mb-1">
                            Unit cost for the company (excluding VAT). Must be ≤ the sale price.<br />
                            Min: 0.01, Max: 5,000.00. Must be ≤ the sale price.
                        </div> */}
                        <Form.Control
                            type="text"
                            name="gross_weight"
                            value={formik.values.gross_weight}
                            onChange={formik.handleChange}
                            isInvalid={formik.touched.gross_weight && !!formik.errors.gross_weight}
                        />
                        <Form.Control.Feedback type="invalid">
                            {formik.touched.gross_weight && (formik.errors.gross_weight?.toString() ?? '')}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Net Weight</Form.Label>

                        <Form.Control
                            type="text"
                            name="net_weight"
                            value={formik.values.net_weight}
                            onChange={formik.handleChange}
                            isInvalid={formik.touched.net_weight && !!formik.errors.net_weight}
                        />
                        <Form.Control.Feedback type="invalid">
                            {formik.touched.net_weight && (formik.errors.net_weight?.toString() ?? '')}
                        </Form.Control.Feedback>
                    </Form.Group>

                    

                    <Form.Group className="mb-3">
                        <Form.Label>B/L Number</Form.Label>

                        <Form.Control
                            type="text"
                            name="bill_lading_number"
                            value={formik.values.bill_lading_number}
                            onChange={formik.handleChange}
                            isInvalid={formik.touched.bill_lading_number && !!formik.errors.bill_lading_number}
                        />
                        <Form.Control.Feedback type="invalid">
                            {formik.touched.bill_lading_number && (formik.errors.bill_lading_number?.toString() ?? '')}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Shipping Mode</Form.Label>
                        <Form.Control
                            as="select"
                            name="shipping_mode"
                            value={formik.values.shipping_mode}
                            onChange={formik.handleChange}
                            isInvalid={!!formik.touched.shipping_mode && !!formik.errors.shipping_mode}
                        >
                            <option value="">Select shipping mode</option>
                            <option value="truck">Truck</option>
                            <option value="boat">Boat</option>
                        </Form.Control>
                        <Form.Control.Feedback type="invalid">
                            {formik.touched.shipping_mode && formik.errors.shipping_mode as any}
                        </Form.Control.Feedback>
                    </Form.Group>


                    <Form.Group className="mb-3">
                        <Form.Check
                            type="checkbox"
                            label="Is Exported"
                            name="isExported"
                            checked={formik.values.isExported || false}
                            onChange={(e) => formik.setFieldValue('isExported', e.target.checked)}
                        />
                    </Form.Group>


                    <Modal.Footer>
                        <Button variant="secondary" className="custom-close-btn" onClick={handleClose}>
                            {MESSAGES.CANCEL}
                        </Button>
                        <Button variant="primary" type="submit">
                            Save Changes
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default EditInvoiceModal;
